import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MapPin, LinkSimple, PlayCircle } from "@phosphor-icons/react";
import Logo from "../../components/Logo";
import { scrollTo } from "./utils";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────  FOOTER  ───────────────────── */

function UptimeBadge() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/api/public/status`)
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => {});
  }, []);
  if (!status) return null;
  const up = status.state === "operational";
  return (
    <Link
      to="/status"
      data-testid="footer-uptime-badge"
      className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 hover:border-emerald-500/40 transition-colors"
    >
      <span className="relative flex h-2 w-2">
        <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${up ? "bg-emerald-400" : "bg-red-400"}`} />
        <span className={`relative inline-flex h-2 w-2 rounded-full ${up ? "bg-emerald-400" : "bg-red-400"}`} />
      </span>
      <span className="text-[11px] text-neutral-300 tabular-nums">
        {up ? `${status.uptime_90d_pct}% uptime — all systems go` : "Service disruption — details"}
      </span>
    </Link>
  );
}

export function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-white/10 py-12 sm:py-16 bg-black/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <FooterBioLinkBar />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2 md:col-span-1">
            <Logo />
            <p className="text-sm text-neutral-400 mt-4 leading-relaxed max-w-xs">
              Practical AI automation, built for trucking and logistics companies.
            </p>
            <a
              href="mailto:contact@freightpilotai.ai"
              data-testid="footer-email"
              className="text-sm text-[#FF5A1F] hover:text-[#FF7038] mt-4 inline-block"
            >
              contact@freightpilotai.ai
            </a>
            <a
              href="tel:+16056797307"
              data-testid="footer-phone"
              className="text-sm text-neutral-300 hover:text-white mt-1.5 block tabular-nums"
            >
              (605) 679-7307
            </a>
            <UptimeBadge />
          </div>
          <FooterCol
            title="Product"
            links={[
              { label: "Solutions", href: "#solutions", anchor: true },
              { label: "Pricing", to: "/pricing" },
              { label: "Resources", to: "/blog" },
              { label: "Roadmap", to: "/roadmap" },
              { label: "Book a demo", to: "/demo" },
            ]}
          />
          <FooterCol
            title="Company"
            links={[
              { label: "About", href: "#about", anchor: true },
              { label: "Customers", to: "/customers" },
              { label: "Contact", to: "/contact" },
              { label: "Free Assessment", href: "#assessment", anchor: true },
            ]}
          />
          <FooterCol
            title="Trust & Legal"
            links={[
              { label: "Privacy Policy", to: "/privacy" },
              { label: "Terms of Service", to: "/terms" },
              { label: "System Status", to: "/status" },
              { label: "Security", to: "/privacy" },
              { label: "Accessibility", to: "/privacy" },
            ]}
          />
        </div>
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
          <div>© {year} Freight Pilot AI LLC. All rights reserved.</div>
          <div
            data-testid="footer-location"
            className="inline-flex items-center gap-1.5 text-neutral-400"
          >
            <MapPin size={12} weight="fill" className="text-[#FF5A1F]" />
            Built in Sioux Falls, SD · USA
          </div>
        </div>
      </div>
    </footer>
  );
}

export function FooterCol({ title, links }) {
  return (
    <div>
      <div className="overline mb-3 text-neutral-500">{title}</div>
      <ul className="space-y-2">
        {links.map((l) => (
          <li key={l.label}>
            {l.anchor ? (
              <a
                href={l.href}
                onClick={(e) => { e.preventDefault(); scrollTo(l.href.slice(1)); }}
                className="text-sm text-neutral-400 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ) : (
              <Link
                to={l.to}
                className="text-sm text-neutral-400 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * FooterBioLinkBar — surfaces the /watch bio-link menu on every
 * landing-page visit. Fetches the public reels list once, groups by
 * topic, and offers a one-click copy of the paste-ready TikTok bio menu.
 * Renders nothing when no reels are hosted yet (no empty state noise).
 */
function FooterBioLinkBar() {
  const [reels, setReels] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/reels?limit=24`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setReels(data.reels || []);
      } catch { /* silent — footer is optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const bioMenu = useMemo(() => {
    if (!reels.length) return "";
    const byTopic = {};
    for (const r of reels) {
      if (r.topic && !byTopic[r.topic]) byTopic[r.topic] = r;
    }
    const lines = Object.values(byTopic).map(
      (r) => `→ ${r.topic_label}: ${r.signup_url}`,
    );
    return (
      `📌 FreightPilotAI — pick what matters most to you:\n\n` +
      lines.join("\n") +
      `\n\nAll options: https://freightpilotai.ai/watch`
    );
  }, [reels]);

  const copy = async () => {
    if (!bioMenu) return;
    try {
      await navigator.clipboard.writeText(bioMenu);
      toast.success("Bio-link menu copied — paste into TikTok bio");
    } catch {
      toast.error("Couldn't copy — check clipboard permissions");
    }
  };

  if (!reels.length) return null;

  return (
    <div
      data-testid="footer-bio-link-bar"
      className="mb-10 rounded-xl border border-[#FF5A1F]/25 bg-[#FF5A1F]/[0.04] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-bold mb-1">
          On TikTok? Grab our bio-link menu
        </div>
        <div className="text-sm text-white/80 leading-snug">
          One-tap copy of every promo topic's UTM-tagged signup link — paste into your TikTok bio, Beacons, or Linktree in seconds.
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          type="button"
          onClick={copy}
          data-testid="footer-copy-bio-menu"
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold bg-[#FF5A1F] hover:bg-[#FF6A35] text-black transition-colors"
        >
          <LinkSimple size={14} weight="bold" /> Copy bio menu
        </button>
        <Link
          to="/watch"
          data-testid="footer-watch-link"
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-white/10 transition-colors"
        >
          <PlayCircle size={14} weight="bold" /> Watch reels
        </Link>
      </div>
    </div>
  );
}
